/**
 * 端末の充電残量。
 *
 * 船内の共有管理端末（打刻用のタブレット）が電池切れで使えなくなると打刻ができなくなるため、
 * 陸上と運営管理から残量を把握できるようにする。値は既存の端末ハートビート
 * （5分ごと）に相乗りさせて送る。
 *
 * 取得は Chromium 系の Battery Status API（navigator.getBattery）。
 * iOS Safari など未対応のブラウザでは何も送らない（画面側は「—」と出す）。
 */

interface BatteryLike {
  level: number;        // 0〜1
  charging: boolean;
  addEventListener?: (t: string, cb: () => void) => void;
}

export interface BatteryInfo {
  /** 0〜100（整数） */
  level: number;
  charging: boolean;
}

let cached: BatteryInfo | null = null;
let watching = false;

function read(b: BatteryLike): BatteryInfo {
  const pct = Math.max(0, Math.min(100, Math.round((Number(b.level) || 0) * 100)));
  return { level: pct, charging: !!b.charging };
}

/**
 * 現在の充電残量。取れない端末では null。
 * 一度取得できたら値の変化を監視して保持する（毎回 getBattery を呼ばない）。
 */
export async function getBatteryInfo(): Promise<BatteryInfo | null> {
  const getter = (navigator as unknown as { getBattery?: () => Promise<BatteryLike> }).getBattery;
  if (typeof getter !== 'function') return cached;
  try {
    const b = await getter.call(navigator);
    cached = read(b);
    if (!watching && typeof b.addEventListener === 'function') {
      watching = true;
      for (const ev of ['levelchange', 'chargingchange']) {
        b.addEventListener(ev, () => { cached = read(b); });
      }
    }
    return cached;
  } catch {
    return cached;   // 取得に失敗しても前回値があればそれを使う
  }
}

/** ハートビートの本文に足す形にして返す（取れないときは空） */
export async function batteryPayload(): Promise<{ battery_level?: number; battery_charging?: boolean }> {
  const info = await getBatteryInfo();
  if (!info) return {};
  return { battery_level: info.level, battery_charging: info.charging };
}
